// Analyses du portefeuille derrière les matrices, croisements et explorations —
// portage de poc/src/app.js (vues) et poc/src/similarite.js (témoin).
// Règles du POC (ADR-0005) : ordres calculés (R3), groupes de moins de trois
// projets en retrait mais jamais masqués (R5), dates absentes montrées (R6).

import { comparerFr, couvertureParLevier } from "./portefeuille";
import { distanceJaccard, ordonner } from "./seriation";
import type { Poids, Similarites } from "./similarite";
import type { ClasseBioagresseur, Levier, Projet } from "./types";

/** Effectif sous lequel un groupe est en retrait : « à ne pas interpréter ». */
export const EFFECTIF_MINIMAL = 3;

// --- scores -------------------------------------------------------------------

/** Accès en O(1) au score d'une paire ; 1 sur la diagonale. */
export function tableScores(similarites: Similarites): (i: number, j: number) => number {
  const n = similarites.voisins.length;
  const table = new Float64Array(n * n);
  similarites.voisins.forEach((voisins, i) => {
    for (const v of voisins) table[i * n + v.autre] = v.score;
  });
  return (i, j) => (i === j ? 1 : (table[i * n + j] ?? 0));
}

// --- ordres --------------------------------------------------------------------

export interface Ordres {
  /** Projets sériés sur leurs profils de leviers. */
  profils: number[];
  /** Projets sériés sur la ressemblance complète. */
  similarite: number[];
  /** Leviers sériés sur les projets qui les cochent. */
  leviers: number[];
}

const indicesProjetsParLevier = (projets: Projet[], leviers: Levier[]) =>
  leviers.map((l) => new Set(projets.flatMap((p, i) => (p.leviers.includes(l.id) ? [i] : []))));

/** Deux sériations de projets distinctes : elles répondent à deux questions. */
export function calculerOrdres(
  projets: Projet[],
  leviers: Levier[],
  similarites: Similarites,
): Ordres {
  const score = tableScores(similarites);
  const ensembles = projets.map((p) => new Set(p.leviers));
  const parLevier = indicesProjetsParLevier(projets, leviers);
  const vide = new Set<never>();
  return {
    profils: ordonner(projets.length, (i, j) =>
      distanceJaccard(ensembles[i] ?? vide, ensembles[j] ?? vide),
    ),
    similarite: ordonner(projets.length, (i, j) => 1 - score(i, j)),
    leviers: ordonner(leviers.length, (i, j) =>
      distanceJaccard(parLevier[i] ?? vide, parLevier[j] ?? vide),
    ),
  };
}

export const TRIS = [
  { id: "seriation", libelle: "ressemblance (sériation)" },
  { id: "acronyme", libelle: "acronyme" },
] as const;
export type Tri = (typeof TRIS)[number]["id"];

/** Ordre des lignes de la matrice projets × leviers. */
export function ordreProjets(tri: Tri, projets: Projet[], ordres: Ordres): number[] {
  if (tri === "seriation") return [...ordres.profils];
  return projets
    .map((_, i) => i)
    .sort((a, b) => comparerFr(projets[a]?.acronyme ?? "", projets[b]?.acronyme ?? ""));
}

/** Ordre des colonnes de leviers : couverture décroissante. */
export function ordreLeviersParCouverture(projets: Projet[], leviers: Levier[]): number[] {
  return couvertureParLevier(projets, leviers).map((c) => leviers.findIndex((l) => l.id === c.id));
}

// --- pondération ----------------------------------------------------------------

/** Lectures défendables de « se ressembler », comparées par poc/scripts/validation_ponderation.py. */
export const PONDERATIONS: readonly { nom: string; poids: Poids }[] = [
  { nom: "actuelle", poids: { leviers: 0.47, cultures: 0.29, bioagresseurs: 0.24 } },
  { nom: "agronomie d'abord", poids: { leviers: 0.22, cultures: 0.39, bioagresseurs: 0.39 } },
  { nom: "à parts égales", poids: { leviers: 0.34, cultures: 0.33, bioagresseurs: 0.33 } },
  { nom: "leviers d'abord", poids: { leviers: 0.7, cultures: 0.15, bioagresseurs: 0.15 } },
];

export interface Temoin {
  /** Paires parmi les plus proches qui partagent leur plan d'action. */
  memes: number;
  total: number;
  /** Probabilité que deux projets au hasard partagent leur plan d'action. */
  hasard: number;
}

/**
 * Le plan d'action n'entre pas dans le calcul : si les paires les plus proches le
 * partagent quand même, la mesure retrouve des regroupements qu'elle ignore.
 */
export function controlePlansAction(projets: Projet[], similarites: Similarites, n = 20): Temoin {
  const paires = similarites.voisins.flatMap((voisins, a) =>
    voisins.filter((v) => v.autre > a).map((v) => ({ a, b: v.autre, score: v.score })),
  );
  paires.sort((x, y) => x.a - y.a || x.b - y.b);
  const memePlan = (p: { a: number; b: number }) => {
    const plan = projets[p.a]?.planAction;
    return Boolean(plan) && plan === projets[p.b]?.planAction;
  };
  const top = [...paires].sort((x, y) => y.score - x.score).slice(0, n);
  return {
    memes: top.filter(memePlan).length,
    total: top.length,
    hasard: paires.length ? paires.filter(memePlan).length / paires.length : 0,
  };
}

// --- croisements ------------------------------------------------------------------

export interface Groupe {
  libelle: string;
  /** Classe de bioagresseur, pour la couleur ; absente pour une filière. */
  classe?: string;
  projets: Projet[];
  /** Effectif par levier, dans l'ordre des leviers demandé. */
  parLevier: number[];
}

const compter = (projets: Projet[], leviers: Levier[], ordre: number[]) =>
  ordre.map((j) => {
    const id = leviers[j]?.id;
    return projets.filter((p) => id !== undefined && p.leviers.includes(id)).length;
  });

/** Un groupe par classe. Les multi-cibles comptent dans chacune : les totaux ne s'additionnent pas. */
export function croisementBioagresseur(
  projets: Projet[],
  leviers: Levier[],
  classes: ClasseBioagresseur[],
  ordre: number[],
): Groupe[] {
  return classes.map((c) => {
    const dedans = projets.filter((p) => p.bioagresseurs.includes(c.id));
    return {
      libelle: c.libelle,
      classe: c.id,
      projets: dedans,
      parLevier: compter(dedans, leviers, ordre),
    };
  });
}

/** Un groupe par filière, plus « transversal » ; du plus fourni au moins fourni. */
export function croisementFiliere(
  projets: Projet[],
  leviers: Levier[],
  filieres: string[],
  ordre: number[],
): Groupe[] {
  const groupes = filieres.map((f) => ({
    libelle: f,
    projets: projets.filter((p) => p.filiere === f),
  }));
  const transversaux = projets.filter((p) => !p.filiere);
  if (transversaux.length) groupes.push({ libelle: "transversal", projets: transversaux });
  return groupes
    .sort((a, b) => b.projets.length - a.projets.length || comparerFr(a.libelle, b.libelle))
    .map((g) => ({ ...g, parLevier: compter(g.projets, leviers, ordre) }));
}

// --- exploration ---------------------------------------------------------------------

export interface Cooccurrence {
  /** Indices des leviers retenus (indicateurs exclus), dans l'ordre sérié. */
  ordre: number[];
  /** Indicateurs écartés : la co-occurrence avec ce qu'ils résument serait tautologique. */
  ecartes: Levier[];
  /** Projets cochant chaque levier de `ordre`. */
  ensembles: Set<number>[];
}

export function cooccurrenceLeviers(
  projets: Projet[],
  leviers: Levier[],
  ordreLeviers: number[],
): Cooccurrence {
  const ordre = ordreLeviers.filter((k) => leviers[k]?.nature !== "indicateur");
  const parLevier = indicesProjetsParLevier(projets, leviers);
  return {
    ordre,
    ecartes: leviers.filter((l) => l.nature === "indicateur"),
    ensembles: ordre.map((k) => parLevier[k] ?? new Set<number>()),
  };
}

/** Nombre de projets par nombre de leviers cochés, de 0 au total. */
export function distributionLeviers(projets: Projet[], leviers: Levier[]): number[] {
  const effectifs = Array.from({ length: leviers.length + 1 }, () => 0);
  for (const p of projets) {
    const k = Math.min(p.leviers.length, leviers.length);
    effectifs[k] = (effectifs[k] ?? 0) + 1;
  }
  return effectifs;
}

/** Année décimale d'une date ISO (2025-07-01 → 2025,5). */
export const anneeDecimale = (date: string): number =>
  Number(date.slice(0, 4)) + (Number(date.slice(5, 7)) - 1) / 12;

export interface Chronologie {
  /** Projets datés (début ou fin), du plus ancien au plus récent. */
  dates: { projet: Projet; debut: number; fin: number }[];
  sansDate: Projet[];
  min: number;
  max: number;
}

export function chronologie(projets: Projet[]): Chronologie {
  const dates = projets
    .filter((p) => p.dateDebut || p.dateFin)
    .map((projet) => {
      const debut = anneeDecimale(projet.dateDebut ?? projet.dateFin ?? "");
      const fin = anneeDecimale(projet.dateFin ?? projet.dateDebut ?? "");
      return { projet, debut, fin };
    })
    .sort((a, b) => a.debut - b.debut || comparerFr(a.projet.acronyme, b.projet.acronyme));
  const min = dates.length ? Math.floor(Math.min(...dates.map((d) => d.debut))) : 0;
  let max = dates.length ? Math.ceil(Math.max(...dates.map((d) => d.fin))) : 1;
  if (max <= min) max = min + 1;
  return { dates, sansDate: projets.filter((p) => !p.dateDebut && !p.dateFin), min, max };
}
